import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, AvatarGroup, Box, Button, Card, Chip, Divider, LinearProgress, ListItemIcon,
  ListItemText, Menu, MenuItem, Stack, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import StoreIcon from '@mui/icons-material/Store';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import EventIcon from '@mui/icons-material/Event';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import {
  addDays, dayLabel, eventPeople, eventRequest, eventTime, mealText, mealWarning, money, nearDay, slotTitle, today, weekStart as startOfWeek, weekTitle,
} from '../labels';
import {
  CardGrid, Loader, MemberAvatar, NeedsHousehold, PageHeader, Panel, WeekNavigator, useHousehold, useLoad, useNotify, useRun, useUndoable,
} from '../common';
import { TaskActions, TaskRow, useSheets, useTaskActions } from '../tasks';
import { SoupRow, useSoupActions } from '../meals/SoupRow';

interface WeekDay {
  date: T.DateOnly;
  isToday: boolean;
  lunch?: T.MealSlot;
  dinner?: T.MealSlot;
  events: T.CalendarEvent[];
  tasks: T.TaskOccurrence[];
  trips: T.TripSummary[];
}

const ALERT_CHIP: Record<T.WeekAlertKind, string> = {
  LeftoversWithoutSolution: 'Sobras',
  EmptyMealsSoon: 'Por planear',
  MissingIngredient: 'Falta',
  MealPlanChanged: 'Ementa mudou',
  OverdueTasks: 'Atrasada',
  NoPotSoon: 'Sopa',
};

const SOLVE_TEXT: Partial<Record<T.WeekAlertKind, string>> = {
  MealPlanChanged: 'Rever a lista de compras',
  MissingIngredient: 'Resolver',
  EmptyMealsSoon: 'Planear a refeição',
  LeftoversWithoutSolution: 'Decidir as sobras',
  NoPotSoon: 'Marcar sopa',
};

/** "planeada · 12 artigos", as Android's TripSummary.Subtitle. */
function tripSubtitle(trip: T.TripSummary): string {
  switch (trip.status) {
    case 'Planned': return `planeada · ${trip.itemCount} artigos`;
    case 'InProgress': return `em curso · faltam ${trip.pendingCount} · carrinho ${money(trip.cartTotal)}`;
    case 'Paid': return `paga · ${money(trip.receiptTotal ?? trip.cartTotal)}`;
    default: return 'cancelada';
  }
}

/** "2 tarefas por fazer · compras · Continente". */
function todaySummary(day: WeekDay): string {
  const pending = day.tasks.filter(t => t.status === 'Pending').length;
  const parts = day.events.length === 0 ? [] : [day.events.length === 1 ? '1 evento' : `${day.events.length} eventos`];
  parts.push(pending === 0 ? 'Nada por fazer' : pending === 1 ? '1 tarefa por fazer' : `${pending} tarefas por fazer`);
  day.trips.forEach(t => parts.push(`compras · ${t.storeName}`));
  return parts.join(' · ');
}

const START_HIDDEN_KEY = 'planner.startCardHidden';
const readHidden = () => { try { return localStorage.getItem(START_HIDDEN_KEY) === '1'; } catch { return false; } };
const writeHidden = () => { try { localStorage.setItem(START_HIDDEN_KEY, '1'); } catch { /* not kept */ } };

interface StartStep { done: boolean; text: string; detail: string; path: string; }

/** "Para começar": the first things to set up, each ticked when done; gone when all are done or hidden. */
function StartCard({ overview }: { overview: T.WeekOverview }) {
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(readHidden);
  const [steps, setSteps] = useState<StartStep[] | null>(null);
  const planned = overview.plan.slots.some(s => s.kind !== 'Empty');

  useEffect(() => {
    if (hidden) return;
    let alive = true;
    api.getWeekSetup().then(setup => {
      if (!alive) return;
      const list: StartStep[] = [
        { done: setup.recipeCount >= 3, text: 'Junta as receitas que costumas fazer', detail: 'Bastam o nome e os ingredientes', path: '/casa/receitas' },
        { done: setup.hasStores, text: 'Diz em que lojas compras', detail: 'A lista separa-se por loja', path: '/casa/compras/lojas' },
        { done: planned, text: 'Planeia a semana', detail: 'Ou pede sugestões no Plano', path: '/casa/refeicoes' },
        { done: setup.hasStock, text: 'Diz o que tens em casa', detail: 'Por voz: "tenho 6 latas de atum na despensa"', path: '/casa/em-casa' },
      ];
      if (list.every(s => s.done)) { writeHidden(); setHidden(true); return; }
      setSteps(list);
    }).catch(() => alive && setSteps(null)); // not worth an error on the home screen
    return () => { alive = false; };
  }, [hidden, planned]);

  if (hidden || !steps) return null;
  return (
    <Panel
      title={`Para começar · ${steps.filter(s => s.done).length} de ${steps.length}`}
      action={<Button size="small" color="inherit" onClick={() => { writeHidden(); setHidden(true); }}>Esconder</Button>}
      sx={{ pb: 1 }}
    >
      <LinearProgress variant="determinate" value={100 * steps.filter(s => s.done).length / steps.length} sx={{ mx: 2, my: 1, height: 6, borderRadius: 3 }} />
      {steps.map(step => (
        <Box
          key={step.text}
          onClick={step.done ? undefined : () => navigate(step.path)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 0.75, minHeight: 44, cursor: step.done ? 'default' : 'pointer', '&:hover': step.done ? undefined : { bgcolor: 'action.hover' } }}
        >
          {step.done ? <CheckCircleIcon color="success" fontSize="small" /> : <RadioButtonUncheckedIcon color="primary" fontSize="small" />}
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="body2" sx={{ textDecoration: step.done ? 'line-through' : undefined, color: step.done ? 'text.secondary' : undefined, fontWeight: step.done ? 400 : 550 }}>{step.text}</Typography>
            {!step.done && <Typography variant="caption" color="text.secondary">{step.detail}</Typography>}
          </Box>
          {!step.done && <ChevronRightIcon fontSize="small" color="action" />}
        </Box>
      ))}
    </Panel>
  );
}

/** "Semana garantida?": meals covered, what to buy at each store, what to confirm and what runs out before the trip. */
function ReadinessCard({ readiness }: { readiness?: T.WeekReadiness | null }) {
  const navigate = useNavigate();
  if (!readiness || (readiness.mealsPlanned === 0 && readiness.toBuyCount === 0 && readiness.uncertainCount === 0 && readiness.runningOut.length === 0))
    return null;
  const allCovered = readiness.mealsCovered === readiness.mealsPlanned;
  const [verdict, color]: [string, 'success' | 'warning' | 'error'] = allCovered && readiness.runningOut.length === 0
    ? (readiness.uncertainCount === 0 ? ['sim', 'success'] : ['quase', 'warning'])
    : ['ainda não', 'error'];
  return (
    <Panel title={<>Semana garantida? <Chip size="small" color={color} label={verdict} sx={{ ml: 0.5 }} /></>} sx={{ pb: 1.5 }}>
      <Stack spacing={0.25} sx={{ px: 2 }}>
        {readiness.mealsPlanned > 0 && (
          <Typography variant="body2" color={allCovered ? undefined : 'warning.dark'}>
            Refeições cobertas: {readiness.mealsCovered} de {readiness.mealsPlanned}
          </Typography>
        )}
        <Typography variant="body2">
          {readiness.toBuyCount === 0 ? 'Nada para comprar' : `Para comprar: ${readiness.toBuyCount} · `
            + readiness.stores.map(s => `${s.name} ${s.toBuyCount} · ${s.nextTripDate ? nearDay(s.nextTripDate) : 'sem ida'}`).join(', ')}
        </Typography>
        {readiness.uncertainCount > 0 && (
          <Typography variant="body2">
            {readiness.uncertainCount === 1 ? '1 artigo por confirmar' : `${readiness.uncertainCount} artigos por confirmar`}
          </Typography>
        )}
        {readiness.runningOut.slice(0, 3).map(r => (
          <Typography key={r.catalogItemPublicId} variant="body2" color="error">
            {r.name} acaba {nearDay(r.date)} {r.nextTripDate ? `(ida ${nearDay(r.nextTripDate)})` : '(sem ida marcada)'}
          </Typography>
        ))}
        {readiness.runningOut.length > 3 && (
          <Typography variant="body2" color="error">e mais {readiness.runningOut.length - 3} que acabam antes da ida</Typography>
        )}
        <Box sx={{ pt: 1 }}>
          <Button size="small" variant="outlined" onClick={() => navigate('/casa/em-casa/preparar')}>Preparar a semana</Button>
        </Box>
      </Stack>
    </Panel>
  );
}

function MealRow({ label, slot, onOpen }: { label: string; slot: T.MealSlot; onOpen: (slot: T.MealSlot) => void }) {
  return (
    <Box
      role="button" tabIndex={0}
      onClick={() => onOpen(slot)}
      onKeyDown={e => { if (e.key === 'Enter') onOpen(slot); }}
      sx={{ display: 'flex', gap: 1, px: 2, py: 0.75, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: -2 } }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ width: 58, flexShrink: 0, lineHeight: '20px', fontSize: 10.5 }}>{label}</Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={slot.kind === 'Empty' ? { color: 'text.disabled', fontStyle: 'italic' } : { overflowWrap: 'anywhere', fontWeight: 550 }}
        >
          {slot.kind === 'Empty' ? 'Por planear' : slotTitle(slot)}
        </Typography>
        {slot.warnings.map(w => (
          <Typography key={w} variant="caption" component="div" sx={{ color: 'warning.dark' }}>{mealWarning(w)}</Typography>
        ))}
      </Box>
    </Box>
  );
}

/** In the order the API gives them: by start, then title. */
const byStart = (a: T.CalendarEvent, b: T.CalendarEvent) =>
  `${a.date}T${a.startTime ?? ''}|${a.title}`.localeCompare(`${b.date}T${b.startTime ?? ''}|${b.title}`);

/** "15:00–17:00 · Tomás · Creche". */
const eventSubtitle = (event: T.CalendarEvent, members: T.HouseholdMember[]) =>
  [eventTime(event), eventPeople(event, members), event.location, event.feedName].filter(Boolean).join(' · ');

function EventRow({ event, members, onOpen }: { event: T.CalendarEvent; members: T.HouseholdMember[]; onOpen: (event: T.CalendarEvent) => void }) {
  return (
    <Box
      role="button" tabIndex={0} aria-label={`Evento: ${event.title}`}
      onClick={() => onOpen(event)}
      onKeyDown={e => { if (e.key === 'Enter') onOpen(event); }}
      sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, minHeight: 44, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
    >
      <Box sx={{ width: 30, display: 'grid', placeItems: 'center', color: 'primary.main' }}><EventIcon fontSize="small" /></Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 550, overflowWrap: 'anywhere' }}>{event.title}</Typography>
        <Typography variant="caption" color="text.secondary">{eventSubtitle(event, members)}</Typography>
      </Box>
    </Box>
  );
}

function DayCard({ day, members, actions, onOpenSlot, onOpenEvent, dim }: {
  day: WeekDay; members: T.HouseholdMember[]; actions: TaskActions; onOpenSlot: (slot: T.MealSlot) => void;
  onOpenEvent: (event: T.CalendarEvent) => void; dim?: boolean;
}) {
  const navigate = useNavigate();
  const [weekday, date] = dayParts(day.date);
  const hasRows = day.events.length > 0 || day.tasks.length > 0 || day.trips.length > 0;
  return (
    <Card
      sx={theme => ({
        pb: 1, opacity: dim ? 0.7 : 1, transition: 'opacity 120ms',
        '&:hover': { opacity: 1 },
        ...(day.isToday ? { borderColor: 'primary.main', boxShadow: `0 0 0 1px ${theme.palette.primary.main}, 0 8px 24px ${alpha(theme.palette.primary.main, 0.14)}` } : {}),
      })}
    >
      <Box sx={theme => ({
        display: 'flex', alignItems: 'baseline', gap: 1, px: 2, pt: 1.5, pb: 1, mb: 0.5,
        bgcolor: day.isToday ? alpha(theme.palette.primary.main, 0.07) : undefined,
      })}>
        <Typography sx={{ fontWeight: 700, fontSize: 16, color: day.isToday ? 'primary.dark' : undefined }}>{weekday}</Typography>
        <Typography variant="body2" color="text.secondary">{date}</Typography>
        {day.isToday && <Chip size="small" color="primary" label="Hoje" sx={{ ml: 'auto', height: 22 }} />}
      </Box>
      {day.isToday && <Typography variant="caption" color="text.secondary" component="div" sx={{ px: 2, pb: 0.5 }}>{todaySummary(day)}</Typography>}
      {day.lunch && day.dinner && (
        <>
          <MealRow label={mealText('Lunch')} slot={day.lunch} onOpen={onOpenSlot} />
          <MealRow label={mealText('Dinner')} slot={day.dinner} onOpen={onOpenSlot} />
        </>
      )}
      {hasRows && <Divider sx={{ my: 0.75, mx: 2 }} />}
      {day.events.map(e => <EventRow key={e.publicId} event={e} members={members} onOpen={onOpenEvent} />)}
      {day.tasks.map(o => <TaskRow key={o.publicId} occurrence={o} members={members} actions={actions} dense />)}
      {day.trips.map(trip => (
        <Box
          key={trip.publicId} role="button" tabIndex={0}
          onClick={() => navigate(`/casa/idas/${trip.publicId}`)}
          onKeyDown={e => { if (e.key === 'Enter') navigate(`/casa/idas/${trip.publicId}`); }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
        >
          <Box sx={{ width: 30, display: 'grid', placeItems: 'center', color: 'primary.main' }}><ShoppingCartOutlinedIcon fontSize="small" /></Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 550 }}>Compras · {trip.storeName}</Typography>
            <Typography variant="caption" color="text.secondary">{tripSubtitle(trip)}</Typography>
          </Box>
        </Box>
      ))}
    </Card>
  );
}

/** "Segunda-feira, 13 out" → ["Segunda", "13 out"]. */
function dayParts(date: T.DateOnly): [string, string] {
  const title = dayLabel(date);
  const comma = title.indexOf(',');
  if (comma < 0) return [title, ''];
  return [title.substring(0, comma).replace(/-feira$/i, ''), title.substring(comma + 1).trim()];
}

function TaskCard({ title, items, members, actions, warn }: {
  title: string; items: T.TaskOccurrence[]; members: T.HouseholdMember[]; actions: TaskActions; warn?: boolean;
}) {
  return (
    <Panel title={title} tone={warn ? 'warning' : undefined} action={<Typography variant="caption" color="text.secondary">{items.length}</Typography>} sx={{ pb: 1 }}>
      {items.map(o => <TaskRow key={o.publicId} occurrence={o} members={members} actions={actions} dense />)}
    </Panel>
  );
}

/** The "Semana" menu: the same actions and texts as the Android sheet (`WeekPage.xaml.cs`, `OnMoreClicked`). */
const WEEK_ACTIONS: { title: string; hint: string; icon: React.ReactNode; to: (week: T.DateOnly) => string }[] = [
  { title: 'Nova tarefa', hint: 'Uma vez ou a repetir', icon: <TaskAltIcon fontSize="small" />, to: () => '/casa/tarefas/nova' },
  { title: 'Novo evento', hint: 'Uma festa, uma consulta, para quem for', icon: <EventIcon fontSize="small" />, to: () => '/casa/eventos/novo' },
  { title: 'Sugerir as refeições', hint: 'Para as que estão por definir', icon: <AutoAwesomeIcon fontSize="small" />, to: week => `/casa/refeicoes/sugerir?semana=${week}` },
  { title: 'Gerar lista de compras', hint: 'Os ingredientes desta semana que faltam', icon: <ListAltIcon fontSize="small" />, to: week => `/casa/compras/da-ementa?semana=${week}` },
  { title: 'Casa e pessoas', hint: 'Quem vive cá, horas das refeições', icon: <PeopleIcon fontSize="small" />, to: () => '/casa/agregado' },
];

/** Home screen: the household's week — attention, today, overdue, the coming days, the flexible tasks. */
const WeekPage: React.FC = () => {
  const { household, members } = useHousehold();
  const navigate = useNavigate();
  const notify = useNotify();
  const run = useRun();
  const offerUndo = useUndoable();
  const sheets = useSheets();
  const [search, setSearch] = useSearchParams();
  const current = startOfWeek(today());
  const shown = startOfWeek(search.get('semana') ?? current);
  const isCurrentWeek = shown === current;
  const [moreMenu, setMoreMenu] = useState<HTMLElement | null>(null);

  const { data: overview, setData: setOverview, error, loading, reload } = useLoad(() => api.getWeekOverview(shown), [shown]);
  const actions = useTaskActions(members, reload);
  const soup = useSoupActions(overview?.pots, reload);
  const isWide = useMediaQuery(useTheme().breakpoints.up('lg'));

  const goTo = (start: T.DateOnly) => {
    if (start === current) setSearch({}, { replace: true });
    else setSearch({ semana: start }, { replace: true });
  };

  const model = useMemo(() => {
    if (!overview || overview.weekStart.substring(0, 10) !== shown) return null;
    const agenda = overview.agenda;
    const flexible = agenda.occurrences.filter(o => o.windowEnd && o.status === 'Pending' && !o.isOverdue);
    const now = today();
    const days: WeekDay[] = Array.from({ length: 7 }, (_, i) => {
      const date = addDays(shown, i);
      return {
        date,
        isToday: date === now,
        lunch: overview.plan.slots.find(s => s.date === date && s.mealType === 'Lunch'),
        dinner: overview.plan.slots.find(s => s.date === date && s.mealType === 'Dinner'),
        // All-day (and those that started before) first, then by start: the list comes ordered by start
        events: overview.events.filter(e => e.date <= date && e.endDate >= date)
          .sort((a, b) => Number(!a.isAllDay && a.date === date) - Number(!b.isAllDay && b.date === date)),
        // The trip shows as its own row: hide its generated task
        tasks: agenda.occurrences.filter(o => o.date === date && flexible.indexOf(o) < 0 && o.source !== 'ShoppingTrip'),
        trips: overview.trips.filter(t => t.plannedDate === date),
      };
    });
    return {
      // Overdue tasks have their own card, so not repeated as an alert
      alerts: overview.alerts.filter(a => a.kind !== 'OverdueTasks'),
      all: days,
      overdue: isCurrentWeek ? agenda.overdue : [],
      todayDays: days.filter(d => d.isToday),
      coming: isCurrentWeek ? days.filter(d => d.date > now) : days,
      past: isCurrentWeek ? days.filter(d => d.date < now) : [],
      flexible,
    };
  }, [overview, shown, isCurrentWeek]);

  const openSlot = (slotId?: T.Guid | null) => {
    const slot = overview?.plan.slots.find(s => s.publicId === slotId);
    navigate(slot ? `/casa/refeicoes/refeicao/${slot.publicId}?semana=${shown}` : `/casa/refeicoes?semana=${shown}`);
  };

  /** Buy it elsewhere, change the meal, or make it a task. */
  const missingIngredient = async (alert: T.WeekAlert) => {
    const action = await sheets.choose(alert.title, [
      { value: 'store', text: 'Comprar noutra loja', icon: <StoreIcon /> },
      { value: 'meal', text: 'Trocar a refeição', icon: <EditIcon /> },
      { value: 'task', text: 'Criar tarefa para comprar', icon: <CheckIcon /> },
    ], alert.detail);
    switch (action) {
      case 'store': {
        const stores = await run(() => api.getStores());
        if (!stores) return;
        const choice = await sheets.choose('Em que loja?', stores.map(s => ({ value: s.publicId, text: s.name, icon: <StoreIcon /> })));
        const store = stores.find(s => s.publicId === choice);
        if (!store || !alert.needPublicId) return;
        if (await run(() => api.assignShoppingNeedStore(alert.needPublicId!, store.publicId)) === undefined) return;
        notify(`${alert.title}: ${store.name}`);
        break;
      }
      case 'meal':
        openSlot(alert.slotPublicId);
        return;
      case 'task':
        if (!alert.needPublicId) return;
        if (await run(() => api.createNeedTask(alert.needPublicId!)) === undefined) return;
        notify('Tarefa criada');
        break;
      default:
        return;
    }
    await reload();
  };

  /** Sort it out (the usual way for its kind) or stop showing it until something changes. */
  const onAlert = async (alert: T.WeekAlert) => {
    const choice = await sheets.choose(alert.title, [
      { value: 'solve', text: SOLVE_TEXT[alert.kind] ?? 'Abrir', icon: <ArrowForwardIcon />, primary: true },
      { value: 'dismiss', text: 'Não mostrar mais', detail: 'Volta a aparecer se a situação mudar', icon: <CloseIcon /> },
    ], alert.detail);
    if (choice === 'dismiss') {
      if (await run(() => api.dismissWeekAlert(alert)) !== undefined) await reload();
      return;
    }
    if (choice !== 'solve') return;
    switch (alert.kind) {
      case 'MealPlanChanged': navigate(`/casa/compras/da-ementa?semana=${shown}`); break;
      case 'OverdueTasks': navigate('/casa/tarefas'); break;
      case 'MissingIngredient': await missingIngredient(alert); break;
      case 'NoPotSoon': await soup.mark(); break;
      default: openSlot(alert.slotPublicId);
    }
  };

  /** An event: change it, or delete it with "Desfazer". */
  const openEvent = async (event: T.CalendarEvent) => {
    // A read event changes in its own calendar: here only whose it is
    const choice = await sheets.choose(event.title, event.isEditable
      ? [
        { value: 'edit', text: 'Editar', icon: <EditIcon />, primary: true },
        { value: 'delete', text: 'Apagar', icon: <DeleteIcon /> },
      ]
      : [{ value: 'edit', text: 'De quem é', detail: `Vem de "${event.feedName}": o resto muda-se lá`, icon: <PeopleIcon />, primary: true }],
    eventSubtitle(event, members));
    if (choice === 'edit') navigate(`/casa/eventos/${event.publicId}`);
    if (choice !== 'delete') return;
    if (await run(() => api.deleteCalendarEvent(event.publicId)) === undefined) return;
    // Nothing else of the week changes with an event: no reload of the whole week
    setOverview(current => current && { ...current, events: current.events.filter(e => e.publicId !== event.publicId) });
    // Undo puts it back as it was (a new one, same details)
    offerUndo(`Evento "${event.title}" apagado`, () => api.createCalendarEvent(eventRequest(event)),
      restored => setOverview(current => current && { ...current, events: [...current.events, restored].sort(byStart) }));
  };

  const openMeal = (slot: T.MealSlot) => navigate(`/casa/refeicoes/refeicao/${slot.publicId}?semana=${shown}`);
  const dayCard = (d: WeekDay, dim?: boolean) => <DayCard key={d.date} day={d} members={members} actions={actions} onOpenSlot={openMeal} onOpenEvent={openEvent} dim={dim} />;

  const alertsPanel = model && model.alerts.length > 0 && (
    <Panel title="Precisa de atenção" tone="warning" sx={{ pb: 0.75 }}>
      {model.alerts.map(alert => (
        <Box
          key={alert.key} role="button" tabIndex={0}
          onClick={() => onAlert(alert)}
          onKeyDown={e => { if (e.key === 'Enter') onAlert(alert); }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
        >
          <Chip size="small" label={ALERT_CHIP[alert.kind]} color={alert.kind === 'MissingIngredient' ? 'error' : 'warning'} sx={{ flexShrink: 0 }} />
          <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {alert.title}
          </Typography>
          <ChevronRightIcon fontSize="small" color="action" />
        </Box>
      ))}
    </Panel>
  );

  const planned = overview ? overview.plan.slots.filter(s => s.kind !== 'Empty').length : 0;
  const pending = overview ? overview.agenda.occurrences.filter(o => o.status === 'Pending').length : 0;
  const summary = overview && model
    ? `${planned} de ${overview.plan.slots.length} refeições planeadas · ${pending === 1 ? '1 tarefa por fazer' : `${pending} tarefas por fazer`}`
    : undefined;

  return (
    <>
      <PageHeader
        eyebrow={isCurrentWeek ? 'Esta semana' : 'Outra semana'}
        title={household?.name ?? 'Semana'}
        subtitle={summary}
        actions={<>
          {members.length > 0 && (
            <AvatarGroup max={6} sx={{ mr: 1, '& .MuiAvatar-root': { width: 32, height: 32, fontSize: 13 } }}>
              {members.map(m => <Tooltip key={m.publicId} title={m.name}><Box><MemberAvatar member={m} size={32} /></Box></Tooltip>)}
            </AvatarGroup>
          )}
          <Button size="small" variant="outlined" startIcon={<MoreHorizIcon />} sx={{ mr: 1 }} onClick={e => setMoreMenu(e.currentTarget)}>
            Semana
          </Button>
          <WeekNavigator
            title={weekTitle(shown)}
            isCurrent={isCurrentWeek}
            onPrevious={() => goTo(addDays(shown, -7))}
            onNext={() => goTo(addDays(shown, 7))}
            onToday={() => goTo(current)}
          />
        </>}
      />

      {/* The week's other actions, as in the Android "Semana" sheet */}
      <Menu anchorEl={moreMenu} open={moreMenu !== null} onClose={() => setMoreMenu(null)}>
        {WEEK_ACTIONS.map(a => (
          <MenuItem key={a.title} onClick={() => { setMoreMenu(null); navigate(a.to(shown)); }}>
            <ListItemIcon>{a.icon}</ListItemIcon>
            <ListItemText primary={a.title} secondary={a.hint} />
          </MenuItem>
        ))}
      </Menu>

      <Loader loading={loading} error={error} onRetry={reload} hasData={model !== null}>
        {model && overview && household && (isWide ? (
          // Desktop: the whole week at a glance, what needs a decision on the side
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: 3, alignItems: 'start' }}>
            <Stack spacing={2}>
              <SoupRow week={overview.pots} onChanged={reload} />
              <CardGrid min={225}>
                {model.all.map(d => dayCard(d, isCurrentWeek && d.date < today()))}
              </CardGrid>
            </Stack>
            <Stack spacing={2} sx={{ position: 'sticky', top: 24 }}>
              <StartCard overview={overview} />
              {alertsPanel}
              <ReadinessCard readiness={overview.readiness} />
              {model.overdue.length > 0 && <TaskCard title="Tarefas em atraso" items={model.overdue} members={members} actions={actions} warn />}
              {model.flexible.length > 0 && <TaskCard title="Esta semana, sem dia fixo" items={model.flexible} members={members} actions={actions} />}
              {model.alerts.length === 0 && model.overdue.length === 0 && (
                <Panel sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckCircleIcon color="success" />
                  <Box>
                    <Typography sx={{ fontWeight: 650, fontSize: 15 }}>Tudo em dia</Typography>
                    <Typography variant="caption" color="text.secondary">Nada em atraso nem por resolver.</Typography>
                  </Box>
                </Panel>
              )}
            </Stack>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            <StartCard overview={overview} />
            {alertsPanel}
            <ReadinessCard readiness={overview.readiness} />
            <SoupRow week={overview.pots} onChanged={reload} />
            {model.todayDays.map(d => dayCard(d))}
            {model.overdue.length > 0 && <TaskCard title="Tarefas em atraso" items={model.overdue} members={members} actions={actions} warn />}
            <CardGrid min={300}>{model.coming.map(d => dayCard(d))}</CardGrid>

            {model.past.length > 0 && (
              <Accordion disableGutters variant="outlined">
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>Dias anteriores</Typography>
                    <Typography variant="caption" color="text.secondary">{model.past.length === 1 ? '1 dia' : `${model.past.length} dias`}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>{model.past.map(d => dayCard(d))}</Stack>
                </AccordionDetails>
              </Accordion>
            )}

            {model.flexible.length > 0 && <TaskCard title="Esta semana, sem dia fixo" items={model.flexible} members={members} actions={actions} />}
          </Stack>
        ))}
      </Loader>
      {actions.element}
      {sheets.element}
      {soup.element}
    </>
  );
};

const WeekRoute: React.FC = () => <NeedsHousehold><WeekPage /></NeedsHousehold>;
export default WeekRoute;
